/**
 * A simple collection of functions to display toast notifications.  There are short messages that are momentarily
 * displayed in the top right corner of our application in response to a user action or API error/warning.  The user can
 * just wait for the toast to vanish (after a few seconds) or dismiss it using its close button.
 *
 * The simplest form of toast notification displays a text message that tells the user what has happened, with
 * background color that indicates the level of importance/severity - success (green), warning (yellow), error (red),
 * info (blue).
 *
 * The more sophisticated notifications include an action button to allow the user take a secondary action
 * (typically a sort of Undo or Restore operation).
 */

import { isCancelledError } from '@tanstack/react-query';
import clsx from 'clsx';
import HttpCodes from 'http-status-codes';
import i18next from 'i18next';
import { get, isError } from 'lodash';
import { Bounce, cssTransition, toast } from 'react-toastify';
import { isProtractor } from '@/core/utilities';
import { extractError } from '@/requests/axios.utilities';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { doTrack } from '@/track/trackProxy.service';
import { logError, logInfo, logWarn } from '@/utilities/logger';
import { formatError, formatValue } from '@/utilities/logger.utilities';
import { Button } from '@seeqdev/qomponents';
import type { TOptions } from 'i18next';

import type { ToastOptions, TypeOptions } from 'react-toastify';
import type { TrackInformation } from '@/track/track.types';
import type { ButtonVariant, IconType } from '@seeqdev/qomponents';

const systemTestTransition = cssTransition({
  enter: 'Toastify--animate Toastify_systemTest',
  exit: 'Toastify--animate Toastify_systemTest',
});
/***
 * Type definitions
 */

type BaseToast = {
  toastUUID?: string;
  doNotTrack?: boolean;
  /** Preserve line breaks in the message. Opt-in, so single-line toasts keep collapsing stray newlines. */
  multiline?: boolean;
};

export type TextToastProps = BaseToast & {
  messageKey: string;
  messageParams?: TOptions;
};

export type ApiToastProps = BaseToast & {
  httpResponseOrError: unknown;
  displayForbidden?: true;
};

export type ButtonToastProps = (TextToastProps | ApiToastProps) & {
  buttonId?: string;
  buttonLabelKey: string;
  buttonLabelParams?: TOptions;
  buttonAction: () => void;
  buttonIcon?: string | null;
  buttonIconStyle?: IconType;
  buttonVariant?: ButtonVariant;
};

export type ToastProps = TextToastProps | ApiToastProps | ButtonToastProps;

/**
 * Internals
 */

const suppressDisplay = headlessRenderMode(); // CRAB-27446: we're no longer handling route-based suppression

function isTextToast(props: ToastProps): props is TextToastProps {
  return 'messageKey' in props;
}

function isButtonToast(props: ToastProps): props is ButtonToastProps {
  return 'buttonLabelKey' in props;
}

function renderKind(
  method: TypeOptions,
  { toastUUID }: ToastProps,
): { kind: 'suppress' | 'new' | 'update'; toastId: string } {
  if (suppressDisplay) return { kind: 'suppress', toastId: '' };
  if (toastUUID && toast.isActive(toastUUID)) return { kind: 'update', toastId: toastUUID };
  return { kind: 'new', toastId: toastUUID ?? `${method}|${Date.now()}` };
}

function apiLog(method: TypeOptions, props: ApiToastProps): string {
  const errorOrResponse = extractError(props.httpResponseOrError);
  const text = isError(errorOrResponse) ? formatError(errorOrResponse) : formatValue(errorOrResponse);
  try {
    logAndTrack(text, method, props);
  } catch (e) {
    // Do not fail toast rendering if logging fails (which can happen if server is down)
  }
  return text;
}

function autoClose(method: TypeOptions, options?: ToastOptions): number | false {
  const autoCloseInterval1 = method === 'error' ? AUTO_CLOSE_INTERVAL_LONG : AUTO_CLOSE_INTERVAL_DEFAULT;
  return options?.autoClose === undefined ? autoCloseInterval1 : options.autoClose;
}

function getToastOptions(
  toastId: string,
  method: TypeOptions,
  autoClose: number | false | undefined,
  options?: ToastOptions,
): ToastOptions {
  return {
    draggable: false,
    closeOnClick: false,
    closeButton: true,
    ...options,
    toastId,
    autoClose,
    transition: isProtractor() ? systemTestTransition : Bounce,
    hideProgressBar: false,
  };
}

/**
 * The body of every toast. Line breaks are preserved only for callers that opt in via `multiline`,
 * so existing single-line messages that happen to contain a newline keep their current layout.
 */
const ToastText = ({ text, className, multiline }: { text: string; className?: string; multiline?: boolean }) => (
  <div className={clsx(multiline && 'tw:whitespace-pre-line', className)} data-testid="toastText">
    {text}
  </div>
);

function renderSimpleToast(
  text: string,
  method: Exclude<TypeOptions, 'default'>,
  props: ToastProps,
  options?: ToastOptions,
): string | number | null {
  const { kind, toastId } = renderKind(method, props);
  const content = <ToastText multiline={props.multiline} text={text} />;

  if (kind === 'new') {
    return toast[method](content, getToastOptions(toastId, method, autoClose(method, options), options));
  }

  if (kind === 'update') {
    toast.update(toastId, { render: content });
    return toastId;
  }

  return null;
}

function apiToast(
  method: Exclude<TypeOptions, 'default'>,
  props: ApiToastProps,
  options?: ToastOptions,
): string | number | null {
  const text = apiLog(method, props);
  return renderSimpleToast(text, method, props, options);
}

function textLog(method: TypeOptions, props: TextToastProps): string {
  // NOTE: we force English translation for log text!
  const text = i18next.t(props.messageKey, {
    ...props.messageParams,
    lng: 'en',
  });
  logAndTrack(text, method, props);
  return i18next.t(props.messageKey, props.messageParams);
}

function textToast(
  method: Exclude<TypeOptions, 'default'>,
  props: TextToastProps,
  options?: ToastOptions,
): string | number | null {
  const text = textLog(method, props);
  return renderSimpleToast(text, method, props, options);
}

function buttonToast(
  method: Exclude<TypeOptions, 'default'>,
  props: ButtonToastProps,
  options?: ToastOptions,
): string | number | null {
  const text = isTextToast(props) ? textLog(method, props) : apiLog(method, props);
  const { kind, toastId } = renderKind(method, props);
  const buttonId = props.buttonId ?? `toastActionButton_${toastId}`;
  const autoCloseInterval =
    options?.autoClose === false || options?.autoClose === undefined
      ? options?.autoClose
      : Math.max(options?.autoClose, AUTO_CLOSE_INTERVAL_LONG);
  const autoClose = options?.autoClose === undefined ? AUTO_CLOSE_INTERVAL_LONG : autoCloseInterval;

  return kind === 'suppress'
    ? null
    : toast[method](
        <div className="button-toast-wrapper flexRowContainer tw:items-center">
          <ToastText className="tw:mb-sq-10" multiline={props.multiline} text={text} />
          <Button
            onClick={() => {
              toast.dismiss(props.toastUUID);
              props.buttonAction();
            }}
            label={
              props.buttonLabelKey
                ? (i18next.t(props.buttonLabelKey, props.buttonLabelParams) as string)
                : (i18next.t('UNDO') as string)
            }
            testId="toastActionButton"
            id={buttonId}
            icon={props.buttonIcon ?? 'fa-undo'}
            iconStyle={props.buttonIconStyle ?? 'white'}
            variant={props.buttonVariant ?? 'theme'}
          />
        </div>,
        getToastOptions(toastId, method, autoClose, options),
      );
}

function buildToast(
  method: Exclude<TypeOptions, 'default'>,
  props: ToastProps,
  options?: ToastOptions,
): string | number | null {
  if (isButtonToast(props)) return buttonToast(method, props, options);
  if (isTextToast(props)) return textToast(method, props, options);
  return apiToast(method, props, options);
}

function log(method: TypeOptions, message: string) {
  switch (method) {
    case 'success':
    case 'info':
      logInfo(message);
      break;
    case 'warning':
      logWarn(message);
      break;
    case 'error':
      logError(message);
      break;
  }
}

function getToastTrackInformation(text: string, props: ToastProps): TrackInformation {
  const trackInformation: TrackInformation = {
    information: text,
  };
  if (isTextToast(props)) {
    Object.entries(props.messageParams ?? {}).forEach(([key, value]) => {
      trackInformation[key] = value as string;
    });
  }
  return trackInformation;
}

function logAndTrack(text: string, method: TypeOptions, props: ToastProps) {
  log(method, text);
  if (!props.doNotTrack) {
    const trackInformation = getToastTrackInformation(text, props);
    doTrack('Warning', `${method}${suppressDisplay ? '(suppressed)' : ''}`, trackInformation);
  }
}

/**
 * Exported functions and constants
 */

/**
 * Renders an informational toast
 * @param props
 * @param options
 * @returns the toast id
 */
export function infoToast(props: ToastProps, options?: ToastOptions): string | number | null {
  return buildToast('info', props, options);
}

/**
 * Renders a success toast
 * @param props
 * @param options
 * @returns the toast id
 */
export function successToast(props: ToastProps, options?: ToastOptions): string | number | null {
  return buildToast('success', props, options);
}

/**
 * Renders a warning toast
 * @param props
 * @param options
 * @returns the toast id
 */
export function warnToast(props: ToastProps, options?: ToastOptions): string | number | null {
  return buildToast('warning', props, options);
}

/**
 * Renders an error toast except for status code SERVICE_UNAVAILABLE,
 * which the backend sends when a request is cancelled.
 * @param props
 * @param options
 * @returns the toast id
 */
export function errorToast(props: ToastProps, options?: ToastOptions): string | number | null {
  if (
    get(props, 'httpResponseOrError.response.status') === HttpCodes.SERVICE_UNAVAILABLE ||
    get(props, 'httpResponseOrError.code') === 'ERR_CANCELED' ||
    isCancelledError(get(props, 'httpResponseOrError'))
  ) {
    return null;
  }

  return buildToast('error', props, options);
}

export const AUTO_CLOSE_INTERVAL_DEFAULT = 4_000;
export const AUTO_CLOSE_INTERVAL_LONG = 10_000;
